import { invoiceLogger } from './invoiceLogger';

const ALLOWED_EU_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
]);

/**
 * NIP Validator - validates Polish NIP (Tax Identification Number) using MOD-11 checksum algorithm.
 *
 * Polish NIP Format:
 * - 10 digits total
 * - First 9 digits: company identifier
 * - Last digit (10th): checksum calculated using MOD-11 algorithm
 *
 * Algorithm:
 * 1. Multiply first 9 digits by weights: [6, 5, 7, 2, 3, 4, 5, 6, 7]
 * 2. Sum all products
 * 3. Calculate modulo 11
 * 4. If result is 10, checksum is 0; otherwise checksum equals the result
 * 5. Compare with 10th digit
 *
 * References:
 * - Polish Ministry of Finance regulations
 * - https://www.gov.pl/web/kas/czym-jest-nip
 */

/**
 * Validates Polish NIP checksum using MOD-11 algorithm.
 * @param nip - NIP string (can contain dashes/spaces, will be cleaned)
 * @returns true if NIP has valid checksum, false otherwise
 *
 * @example
 * isValidPolishNip('123-456-32-18') // true (if checksum is valid)
 * isValidPolishNip('1234563218')     // true (if checksum is valid)
 * isValidPolishNip('0000000000')     // false (invalid checksum)
 * isValidPolishNip('123456789')      // false (too short)
 */
export const isValidPolishNip = (nip: string): boolean => {
  // Clean NIP - remove dashes, spaces, and convert to string
  const cleaned = nip.replace(/[-\s]/g, '');

  // Must be exactly 10 digits
  if (!/^\d{10}$/.test(cleaned)) {
    return false;
  }

  // Reject obvious synthetic values (all digits identical)
  if (/^(\d)\1{9}$/.test(cleaned)) {
    return false;
  }

  // Weights for MOD-11 algorithm
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];

  // Convert string to array of digits
  const digits = cleaned.split('').map(Number);

  // Calculate weighted sum of first 9 digits
  const sum = digits.slice(0, 9).reduce((acc, digit, index) => {
    return acc + digit * weights[index];
  }, 0);

  // Calculate checksum using MOD-11
  const checksum = sum % 11;

  // If checksum is 10, it becomes 0
  const expectedChecksum = checksum === 10 ? 0 : checksum;

  // Compare with 10th digit
  const isValid = digits[9] === expectedChecksum;

  invoiceLogger.debug('[nipValidator] 🔍 Polish NIP validation:', {
    input: nip,
    cleaned,
    weights,
    digits,
    sum,
    checksum,
    expectedChecksum,
    actualChecksum: digits[9],
    isValid,
  });

  return isValid;
};

/**
 * Validates NIP format and checksum (supports dashes/spaces).
 * @param nip - NIP string
 * @returns true if valid Polish NIP with correct checksum
 *
 * @example
 * validatePolishNip('123-456-32-18') // true
 * validatePolishNip('1234563218')    // true
 * validatePolishNip('123-456-32-19') // false (invalid checksum)
 * validatePolishNip('DE123456789')   // false (not Polish NIP)
 */
export const validatePolishNip = (nip?: string | null): boolean => {
  if (!nip || nip.trim() === '') {
    return false; // Empty NIP is invalid
  }

  return isValidPolishNip(nip);
};

/**
 * Validates NIP - supports both Polish NIP (10 digits) and EU VAT numbers.
 * For Polish NIPs, validates checksum using MOD-11.
 * For EU VAT numbers, validates format only (no checksum).
 *
 * @param nip - NIP or VAT-UE string
 * @param skipChecksumValidation - if true, skips MOD-11 checksum validation for Polish NIP
 * @returns true if valid Polish NIP or EU VAT number
 *
 * @example
 * // Polish NIP with checksum validation
 * validateNip('123-456-32-18') // true (if checksum valid)
 * validateNip('0000000000')    // false (invalid checksum)
 *
 * // EU VAT numbers (format only)
 * validateNip('DE123456789')   // true
 * validateNip('FR12345678901') // true
 *
 * // Skip checksum validation (format only)
 * validateNip('0000000000', true) // true (format valid, checksum skipped)
 */
export const validateNip = (
  nip?: string | null,
  skipChecksumValidation: boolean = false
): boolean => {
  if (!nip || nip.trim() === '') {
    return true; // Empty NIP is valid (optional field)
  }

  const cleaned = nip.replace(/[-\s]/g, '');

  // Polish NIP: exactly 10 digits
  if (/^\d{10}$/.test(cleaned)) {
    if (skipChecksumValidation) {
      return true; // Format valid, skip checksum
    }
    return isValidPolishNip(cleaned); // Validate checksum
  }

  // EU VAT: 2-letter country code + 8-12 digits
  // Format: AT, BE, BG, CY, CZ, DE, DK, EE, ES, FI, FR, GR, HR, HU, IE, IT, LT, LU, LV, MT, NL, PL, PT, RO, SE, SI, SK
  const normalized = cleaned.toUpperCase();
  const euVatMatch = /^([A-Z]{2})(\d{8,12})$/.exec(normalized);

  if (euVatMatch && ALLOWED_EU_COUNTRY_CODES.has(euVatMatch[1])) {
    return true; // EU VAT format valid
  }

  invoiceLogger.warn('[nipValidator] ❌ Invalid NIP format:', {
    input: nip,
    cleaned,
    isPolishNip: /^\d{10}$/.test(cleaned),
    isEuVat: /^[A-Z]{2}\d{8,12}$/.test(cleaned.toUpperCase()),
  });

  return false;
};

/**
 * Validates NIP for optional fields (allows empty values).
 * Supports both Polish NIP (with checksum) and EU VAT numbers.
 *
 * @param nip - NIP or VAT-UE string (can be null/undefined/empty)
 * @param skipChecksumValidation - if true, skips MOD-11 checksum validation
 * @returns true if empty OR valid NIP/VAT
 *
 * @example
 * validateNipOptional('')             // true (empty is valid)
 * validateNipOptional(null)           // true (null is valid)
 * validateNipOptional('123-456-32-18') // true (if checksum valid)
 * validateNipOptional('DE123456789')  // true (EU VAT)
 * validateNipOptional('0000000000')   // false (invalid checksum)
 */
export const validateNipOptional = (
  nip?: string | null,
  skipChecksumValidation: boolean = false
): boolean => {
  if (!nip || nip.trim() === '') {
    return true; // Empty is valid for optional fields
  }

  return validateNip(nip, skipChecksumValidation);
};

/**
 * Generates error message for invalid NIP.
 * @param nip - Invalid NIP string
 * @returns Human-readable error message
 */
export const getNipErrorMessage = (nip?: string | null): string => {
  if (!nip || nip.trim() === '') {
    return 'NIP jest wymagany';
  }

  const cleaned = nip.replace(/[-\s]/g, '');

  // Check if it's Polish NIP format
  if (/^\d{10}$/.test(cleaned)) {
    if (!isValidPolishNip(cleaned)) {
      return 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.';
    }
  }

  // Check if it's EU VAT format
  if (/^[A-Z]{2}\d{8,12}$/i.test(cleaned)) {
    return 'NIP musi mieć 10 cyfr (polski NIP) lub format VAT-UE (np. DE123456789)';
  }

  // Invalid format
  return 'NIP musi mieć 10 cyfr (możesz użyć myślników) lub format VAT-UE (np. DE123456789)';
};
